import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Building2, User, Phone, Mail, MapPin, Search, X, LayoutGrid, List } from "lucide-react";

const EMPTY = { name: "", contact_name: "", phone: "", email: "", address: "", notes: "" };

export default function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useState(() => localStorage.getItem("clients_view") || "grid");
  const setViewMode = (v) => { setView(v); localStorage.setItem("clients_view", v); };

  const load = useCallback(async () => {
    try { const r = await api.get("/clients"); setClients(r.data); } catch { toast.error("Failed to load clients"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Client name is required"); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/clients/${editing.id}`, form); toast.success("Client updated"); }
      else { await api.post("/clients", form); toast.success("Client added"); }
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await api.delete(`/clients/${c.id}`); toast.success("Client deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? clients.filter((c) =>
        [c.name, c.contact_name, c.email].filter(Boolean).some((v) => v.toLowerCase().includes(q)))
    : clients;

  return (
    <div className="space-y-6 fade-up" data-testid="clients-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Directory</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading flex items-center gap-2">
            <Building2 className="h-8 w-8 text-lime-500" /> Clients
          </h1>
          <p className="text-slate-500 mt-1">{clients.length} client{clients.length !== 1 ? "s" : ""} in your directory.</p>
        </div>
        <Button data-testid="new-client-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white"><Plus className="h-4 w-4 mr-1.5" /> New Client</Button>
      </div>

      {!loading && clients.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative max-w-sm flex-1 min-w-[220px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="client-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, contact or email…" className="pl-9 pr-9" />
            {search && (
              <button data-testid="client-search-clear" onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
            )}
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid="client-view-toggle">
            <button onClick={() => setViewMode("grid")} data-testid="client-view-grid" aria-label="Tile view"
              className={`px-3 py-2 flex items-center gap-1.5 text-sm ${view === "grid" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
              <LayoutGrid className="h-4 w-4" /> Tiles
            </button>
            <button onClick={() => setViewMode("list")} data-testid="client-view-list" aria-label="List view"
              className={`px-3 py-2 flex items-center gap-1.5 text-sm border-l border-slate-200 ${view === "list" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
              <List className="h-4 w-4" /> List
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>
      ) : clients.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center text-slate-500">No clients yet. Add your first client.</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center text-slate-500" data-testid="client-no-results">No clients match "{search}".</div>
      ) : view === "list" ? (
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden" data-testid="clients-list">
          {filtered.map((c) => (
            <div key={c.id} onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-row-${c.id}`}
              className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50 cursor-pointer">
              <span className="h-9 w-9 rounded-lg bg-lime-100 text-lime-700 flex items-center justify-center shrink-0"><Building2 className="h-4 w-4" /></span>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900 truncate">{c.name}</p>
                <p className="text-xs text-slate-500 truncate">{[c.contact_name, c.email].filter(Boolean).join(" · ") || "—"}</p>
              </div>
              <span className="hidden sm:block text-sm text-slate-500 truncate w-40">{c.phone || ""}</span>
              <div className="flex gap-1 shrink-0">
                <button onClick={(e) => { e.stopPropagation(); openEdit(c); }} data-testid={`client-edit-${c.id}`} className="h-8 w-8 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                <button onClick={(e) => { e.stopPropagation(); remove(c); }} data-testid={`client-delete-${c.id}`} className="h-8 w-8 rounded-md flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <div key={c.id} onClick={() => navigate(`/clients/${c.id}`)} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:border-slate-300 transition-all cursor-pointer" data-testid={`client-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading font-semibold text-slate-900">{c.name}</h3>
                <div className="flex gap-1">
                  <button onClick={(e) => { e.stopPropagation(); openEdit(c); }} data-testid={`client-edit-${c.id}`} className="h-8 w-8 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                  <button onClick={(e) => { e.stopPropagation(); remove(c); }} data-testid={`client-delete-${c.id}`} className="h-8 w-8 rounded-md flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                {c.contact_name && <p className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" />{c.contact_name}</p>}
                {c.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" />{c.phone}</p>}
                {c.email && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /><span className="truncate">{c.email}</span></p>}
                {c.address && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /><span className="truncate">{c.address}</span></p>}
              </div>
              {c.notes && <p className="mt-3 text-xs text-slate-500 border-t border-slate-100 pt-2">{c.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? "Edit Client" : "New Client"}</DialogTitle><DialogDescription>Client contact details.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Client / Company Name</Label>
              <Input data-testid="client-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" placeholder="e.g. Gympie Cane Co." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Contact Name</Label>
                <Input data-testid="client-contact-input" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="mt-1.5" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</Label>
                <Input data-testid="client-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" /></div>
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
              <Input data-testid="client-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Address</Label>
              <Input data-testid="client-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label>
              <Textarea data-testid="client-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5" rows={2} /></div>
            <Button data-testid="save-client-btn" onClick={save} disabled={saving} className="w-full bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold">{saving ? "Saving…" : editing ? "Update Client" : "Add Client"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
